/**
 * Kontextové menu na mape, ktoré sa vždy zmestí na obrazovku.
 *
 * Doteraz sa menu kreslilo priamo na súradnice kliknutia (`position: fixed`,
 * left/top = clientX/clientY). Keď technik klikol na stĺpik pri spodnom
 * okraji — a na mape je to bežné, lebo si mapu posunie tak, aby mal stĺpik
 * pred sebou — menu vyšlo pod obrazovku a položky sa nedali ani vybrať,
 * ani doscrolovať (má position: fixed, stránka sa nehýbe).
 *
 * Riešenie je to, čo robí každé natívne menu:
 *   - vpravo sa nezmestí → vyklopí sa doľava od kurzora,
 *   - dole sa nezmestí → vyklopí sa nahor,
 *   - nezmestí sa ani nahor (dlhé menu na nízkom telefóne) → prilepí sa
 *     k hornému okraju a dostane vlastné rolovanie.
 * Meria sa po vykreslení (useLayoutEffect), takže sa nikdy neukáže na
 * zlom mieste a až potom neposkočí.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';

interface PlavajuceMenuProps {
  x: number;
  y: number;
  className?: string;
  children: React.ReactNode;
}

const OKRAJ = 8;

export interface Rozmer {
  x: number; y: number;          // kam sa kliklo
  sirka: number; vyska: number;  // veľkosť menu
  sirkaOkna: number; vyskaOkna: number;
}

/**
 * Samotný výpočet je oddelený od komponentu, nech sa dá overiť bez
 * prehliadača — v appke aj v teste beží presne tento kód.
 */
export const vypocitajPolohu = (
  { x, y, sirka, vyska, sirkaOkna, vyskaOkna }: Rozmer,
): { left: number; top: number; maxHeight?: number } => {
  let left = x;
  if (left + sirka > sirkaOkna - OKRAJ) left = x - sirka;          // vyklopiť doľava
  if (left < OKRAJ) left = OKRAJ;
  if (left + sirka > sirkaOkna - OKRAJ) left = Math.max(OKRAJ, sirkaOkna - OKRAJ - sirka);

  let top = y;
  let maxHeight: number | undefined;
  const miestoDole = vyskaOkna - y - OKRAJ;
  const miestoHore = y - OKRAJ;

  if (vyska > miestoDole) {
    if (vyska <= miestoHore) {
      top = y - vyska;                       // vyklopiť nahor
    } else {
      top = OKRAJ;                           // nezmestí sa ani tak — zhora a rolovať
      maxHeight = vyskaOkna - 2 * OKRAJ;
    }
  }
  if (top < OKRAJ) top = OKRAJ;

  return { left, top, maxHeight };
};

export const PlavajuceMenu: React.FC<PlavajuceMenuProps> = ({
  x, y, className, children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [poloha, setPoloha] = useState<{ left: number; top: number; maxHeight?: number }>({
    left: x, top: y,
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Meria sa bez obmedzenia výšky, nech vieme skutočnú veľkosť menu.
    const r = el.getBoundingClientRect();

    setPoloha(vypocitajPolohu({
      x, y,
      sirka: r.width,
      vyska: r.height,
      sirkaOkna: window.innerWidth,
      vyskaOkna: window.innerHeight,
    }));
  }, [x, y, children]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        position: 'fixed',
        left: poloha.left,
        top: poloha.top,
        maxHeight: poloha.maxHeight,
        overflowY: poloha.maxHeight ? 'auto' : undefined,
        WebkitOverflowScrolling: 'touch',
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};
